/*
  Journey Editor Selectors

  This module provides Redux selectors for accessing journey editor state
  in a clean, memoized way.
*/

import { createSelector } from '@reduxjs/toolkit';
import type { Node, Edge } from 'react-flow-renderer';
import type { State } from '~/store';
import type { ExpansionBounds } from './state';
import type { JourneyWorkflowMetadata } from '../types';
import { selectWorkflowGraph } from '~/dashboard/views/workflows/store/redux/selectors/graph';
import { mergeWorkflowIntoJourney } from './graphMerge';
import { convertDocumentToReactFlow } from '../engine';
import type { RootStateWithJourneyEditor } from './types';

// Root state type (assuming the slice is mounted at 'journeyEditor')
// Using type alias for backward compatibility
type RootState = RootStateWithJourneyEditor;

/**
 * Select the entire journey editor state
 */
export const selectJourneyEditorState = (state: RootState) => state.journeyEditor;

/**
 * Select the current document model
 */
export const selectDocument = createSelector(
  [selectJourneyEditorState],
  state => state.doc,
);

/**
 * Select the journey editor props (reference data)
 */
export const selectJourneyProps = createSelector(
  [selectJourneyEditorState],
  state => state.props,
);

/**
 * Select workflows from props
 */
export const selectWorkflowsFromProps = createSelector(
  [selectJourneyProps],
  props => props?.workflows || [],
);

/**
 * Select actions from props
 */
export const selectActionsFromProps = createSelector(
  [selectJourneyProps],
  props => props?.actions || [],
);

/**
 * Select outcomes from props
 */
export const selectOutcomesFromProps = createSelector(
  [selectJourneyProps],
  props => props?.outcomes || [],
);

/**
 * Select outcome customer_outcome_id by outcome name
 * Returns null if not found
 */
export const selectOutcomeIdByName = createSelector(
  [selectOutcomesFromProps, (_state: RootState, outcomeName: string) => outcomeName],
  (outcomes, outcomeName): number | null => {
    const outcome = outcomes.find(o => o.outcome === outcomeName);
    return outcome?.customer_outcome_id || null;
  },
);

/**
 * Create an outcome map (name -> customer_outcome_id)
 * Useful for batch lookups in mutations
 */
export const selectOutcomeMap = createSelector(
  [selectOutcomesFromProps],
  (outcomes): Record<string, number | null> => {
    const outcomeMap: Record<string, number | null> = {};
    outcomes.forEach(outcome => {
      outcomeMap[outcome.outcome] = outcome.customer_outcome_id || null;
    });
    return outcomeMap;
  },
);

/**
 * Select journey token from document
 */
export const selectJourneyToken = createSelector(
  [selectDocument],
  doc => doc?.token || null,
);

/**
 * Select whether there are unsaved changes
 */
export const selectHasUnsavedChanges = createSelector(
  [selectJourneyEditorState],
  state => state.hasUnsavedChanges,
);

/**
 * Select unified editor metadata
 */
export const selectUnifiedState = createSelector(
  [selectJourneyEditorState],
  state => state.unified,
);

export const selectExpandedWorkflowToken = createSelector(
  [selectUnifiedState],
  unified => unified.expandedWorkflowToken,
);

export const selectActiveEditor = createSelector(
  [selectUnifiedState],
  unified => unified.activeEditor,
);

export const selectFocusedJourneyNodeId = createSelector(
  [selectUnifiedState],
  unified => unified.focusedJourneyNodeId,
);

export const selectWorkflowExpansionBounds = createSelector(
  [selectUnifiedState, (_state: RootState, workflowToken: string) => workflowToken],
  (unified, workflowToken): ExpansionBounds | undefined => unified.expansionBounds[workflowToken],
);

export const selectJourneyWorkflows = createSelector(
  [selectUnifiedState],
  unified => unified.workflows as Record<string, JourneyWorkflowMetadata>,
);

/**
 * Select journey workflows from partial state (used in thunks for type safety)
 */
export const selectJourneyWorkflowsFromPartial = (
  state: { journeyEditor: { unified: { workflows: Record<string, JourneyWorkflowMetadata> } } },
): Record<string, JourneyWorkflowMetadata> => state.journeyEditor.unified.workflows;

export const selectWorkflowExpansionBoundsMap = createSelector(
  [selectUnifiedState],
  unified => unified.expansionBounds as Record<string, ExpansionBounds>,
);

/**
 * Select the last API snapshot
 */
export const selectLastApiSnapshot = createSelector(
  [selectJourneyEditorState],
  state => state.lastApiSnapshot,
);

/**
 * Select the current error message
 */
export const selectError = createSelector(
  [selectJourneyEditorState],
  state => state.error,
);

/**
 * Select the current status
 */
export const selectStatus = createSelector(
  [selectJourneyEditorState],
  state => state.status,
);

/**
 * Select whether the editor is currently loading
 */
export const selectIsLoading = createSelector(
  [selectStatus],
  status => status === 'loading',
);

/**
 * Select whether the editor has an error
 */
export const selectHasError = createSelector(
  [selectError],
  error => error !== null,
);

/**
 * Select whether the document is ready (loaded and not in error state)
 */
export const selectIsDocumentReady = createSelector(
  [selectDocument, selectStatus, selectError],
  (doc, status, error) => doc !== null && status !== 'loading' && error === null,
);

/**
 * Select specific nodes by type
 */
export const selectNodesByType = createSelector(
  [selectDocument, (_state: RootState, nodeType: string) => nodeType],
  (doc, nodeType) => {
    if (!doc) return [];
    return Object.values(doc.nodes).filter(node => node.type === nodeType);
  },
);

/**
 * Select outcome nodes specifically
 */
export const selectOutcomeNodes = createSelector(
  [selectDocument],
  doc => {
    if (!doc) return [];
    return Object.values(doc.nodes).filter(node => node.type === 'outcome');
  },
);

/**
 * Select the start node
 */
export const selectStartNode = createSelector(
  [selectDocument],
  doc => {
    if (!doc) return null;
    return doc.nodes[doc.start_node_id] || null;
  },
);

/**
 * Select the terminal reconciliation workflow node
 */
export const selectTerminalReconciliationNode = createSelector(
  [selectDocument],
  doc => {
    if (!doc || !doc.terminal_reconciliation_workflow_node_id) return null;
    return doc.nodes[doc.terminal_reconciliation_workflow_node_id] || null;
  },
);

/**
 * Select edges from the document
 */
export const selectEdges = createSelector(
  [selectDocument],
  doc => {
    if (!doc) return {};
    return doc.edges;
  },
);

/**
 * Select the incoming edge for a specific node
 * Returns the first edge where the target node matches the given nodeId
 */
export const selectIncomingEdge = createSelector(
  [selectEdges, (_state: RootState, nodeId: string) => nodeId],
  (edges, nodeId) => Object.values(edges).find(edge => edge.to.node === nodeId) || null,
);

/**
 * Select the unified graph: journey nodes/edges merged with expanded workflow nodes/edges
 * This creates a single ReactFlow graph that combines both journey and workflow content
 */
export const selectUnifiedGraph = createSelector(
  [
    selectDocument,
    selectJourneyWorkflows,
    selectExpandedWorkflowToken,
    selectWorkflowExpansionBoundsMap,
    (state: State) => selectWorkflowGraph(state),
  ],
  (
    doc,
    workflows,
    expandedWorkflowToken,
    boundsMap,
    workflowGraph,
  ): { nodes: Node[]; edges: Edge[] } => {
    // Convert journey document to ReactFlow graph
    // Pass workflows so terminal recon synthetics can be generated
    const journeyGraph = doc
      ? convertDocumentToReactFlow(doc, workflows)
      : { nodes: [], edges: [] };

    // If no workflow is expanded, return journey graph as-is
    if (!expandedWorkflowToken) {
      return journeyGraph;
    }

    // Get expansion bounds for the expanded workflow
    const bounds = boundsMap[expandedWorkflowToken];
    if (!bounds) {
      // No bounds set yet, return journey graph
      return journeyGraph;
    }

    // Merge workflow into journey
    const merged = mergeWorkflowIntoJourney(
      journeyGraph.nodes,
      journeyGraph.edges,
      workflowGraph.nodes,
      workflowGraph.edges,
      expandedWorkflowToken,
      bounds,
    );

    return merged;
  },
);
